'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
export type CompetitorSize = 'large' | 'medium' | 'small';
export type CompetitorSource = 'manual' | 'google_places' | 'override';

export interface ManualCompetitor {
  id: string;
  name: string;
  lat: number;
  lng: number;
  city: string;
  state: string;
  size: CompetitorSize;
  source: CompetitorSource;
  rating?: number;
  reviewCount?: number;
  vicinity?: string;
  notes?: string;
  annualRevenue?: string;
  marketShare?: string;
  isOverride?: boolean;
  overrideReason?: string;
  createdAt: string;
  // Enriched fields from Google Places Details + Gemini
  phone?: string;
  website?: string;
  formattedAddress?: string;
  businessHours?: string[];
  googleMapsUrl?: string;
  yearsInBusiness?: string;
  knownOwners?: string;
  specialties?: string;
  competitiveWeaknesses?: string;
}

interface CompetitorLocationsManagerProps {
  onCompetitorsChange?: (competitors: ManualCompetitor[]) => void;
  externalCompetitor?: ManualCompetitor | null;
  onExternalAdded?: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const SIZE_CONFIG: Record<CompetitorSize, { label: string; color: string; bg: string; description: string }> = {
  large:  { label: 'Large',  color: '#EF4444', bg: '#FEE2E2', description: '50+ crews, regional dominance' },
  medium: { label: 'Medium', color: '#F59E0B', bg: '#FEF3C7', description: '10–50 crews, local presence' },
  small:  { label: 'Small',  color: '#6B7280', bg: '#F3F4F6', description: '1–10 crews, neighborhood-level' },
};

const SOURCE_CONFIG: Record<CompetitorSource, { label: string; color: string; icon: string }> = {
  manual:        { label: 'Manual Entry',   color: '#7C3AED', icon: 'PencilSquareIcon' },
  google_places: { label: 'Google Places',  color: '#10B981', icon: 'MapPinIcon' },
  override:      { label: 'Override',       color: '#F59E0B', icon: 'ExclamationTriangleIcon' },
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

// ─── Blank form state ─────────────────────────────────────────────────────────
const BLANK_FORM = {
  name: '',
  city: '',
  state: '',
  lat: '',
  lng: '',
  size: 'medium' as CompetitorSize,
  source: 'manual' as CompetitorSource,
  rating: '',
  reviewCount: '',
  vicinity: '',
  notes: '',
  annualRevenue: '',
  marketShare: '',
  isOverride: false,
  overrideReason: '',
};

// ─── DB row → ManualCompetitor ────────────────────────────────────────────────
function rowToCompetitor(row: any): ManualCompetitor {
  return {
    id: row.id,
    name: row.name,
    lat: row.lat,
    lng: row.lng,
    city: row.city,
    state: row.state,
    size: row.size as CompetitorSize,
    source: row.source as CompetitorSource,
    rating: row.rating ?? undefined,
    reviewCount: row.review_count ?? undefined,
    vicinity: row.vicinity ?? undefined,
    notes: row.notes ?? undefined,
    annualRevenue: row.annual_revenue ?? undefined,
    marketShare: row.market_share ?? undefined,
    isOverride: row.is_override ?? false,
    overrideReason: row.override_reason ?? undefined,
    createdAt: row.created_at,
    phone: row.phone ?? undefined,
    website: row.website ?? undefined,
    formattedAddress: row.formatted_address ?? undefined,
    businessHours: row.business_hours ?? undefined,
    googleMapsUrl: row.google_maps_url ?? undefined,
    yearsInBusiness: row.years_in_business ?? undefined,
    knownOwners: row.known_owners ?? undefined,
    specialties: row.specialties ?? undefined,
    competitiveWeaknesses: row.competitive_weaknesses ?? undefined,
  };
}

// ─── ManualCompetitor → DB upsert payload ─────────────────────────────────────
function competitorToRow(c: ManualCompetitor) {
  return {
    id: c.id,
    name: c.name,
    lat: c.lat,
    lng: c.lng,
    city: c.city,
    state: c.state,
    size: c.size,
    source: c.source,
    rating: c.rating ?? null,
    review_count: c.reviewCount ?? null,
    vicinity: c.vicinity ?? null,
    notes: c.notes ?? null,
    annual_revenue: c.annualRevenue ?? null,
    market_share: c.marketShare ?? null,
    is_override: c.isOverride ?? false,
    override_reason: c.overrideReason ?? null,
    phone: c.phone ?? null,
    website: c.website ?? null,
    formatted_address: c.formattedAddress ?? null,
    business_hours: c.businessHours ?? null,
    google_maps_url: c.googleMapsUrl ?? null,
    years_in_business: c.yearsInBusiness ?? null,
    known_owners: c.knownOwners ?? null,
    specialties: c.specialties ?? null,
    competitive_weaknesses: c.competitiveWeaknesses ?? null,
    created_at: c.createdAt,
    updated_at: new Date().toISOString(),
  };
}

// ─── Geocode helper (uses existing geocode API) ───────────────────────────────
async function geocodeAddress(city: string, state: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const res = await fetch(`/api/geocode-address?address=${encodeURIComponent(`${city}, ${state}`)}`);
    if (!res.ok) return null;
    const json = await res.json();
    if (json.lat && json.lng) return { lat: json.lat, lng: json.lng };
    return null;
  } catch {
    return null;
  }
}

// ─── Form Component ───────────────────────────────────────────────────────────
interface CompetitorFormProps {
  initial?: Partial<typeof BLANK_FORM>;
  onSave: (data: typeof BLANK_FORM) => void;
  onCancel: () => void;
  saving: boolean;
  title: string;
  isEdit?: boolean;
}

function CompetitorForm({ initial, onSave, onCancel, saving, title, isEdit }: CompetitorFormProps) {
  const [form, setForm] = useState({ ...BLANK_FORM, ...initial });
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState('');

  const set = (key: keyof typeof BLANK_FORM, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleGeocode = async () => {
    if (!form.city.trim() || !form.state.trim()) return;
    setGeocoding(true);
    setGeocodeError('');
    const result = await geocodeAddress(form.city.trim(), form.state.trim());
    setGeocoding(false);
    if (result) {
      setForm((prev) => ({ ...prev, lat: result.lat.toFixed(5), lng: result.lng.toFixed(5) }));
    } else {
      setGeocodeError('Could not geocode — enter coordinates manually');
    }
  };

  const isValid = form.name.trim() && form.city.trim() && form.state.trim() && form.lat && form.lng;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }}>
      <div
        className="w-full max-w-lg mx-4 rounded-2xl overflow-hidden"
        style={{ background: '#fff', boxShadow: '0 24px 64px rgba(0,0,0,0.22)', maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.25)' }}>
              <Icon name="FlagIcon" size={16} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">{title}</h3>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>Market-specific competitor intelligence</p>
            </div>
          </div>
          <button onClick={onCancel} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/10" style={{ color: 'rgba(255,255,255,0.7)' }}>
            <Icon name="XMarkIcon" size={16} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>Competitor Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Apex Roofing Solutions"
              className="w-full px-3 py-2.5 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-blue-200"
              style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
              autoFocus
            />
          </div>

          {/* City + State */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>City *</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                placeholder="e.g. Columbus"
                className="w-full px-3 py-2.5 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-blue-200"
                style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>State *</label>
              <select
                value={form.state}
                onChange={(e) => set('state', e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-blue-200"
                style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
              >
                <option value="">--</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Coordinates */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold" style={{ color: '#374151' }}>Coordinates *</label>
              <button
                type="button"
                onClick={handleGeocode}
                disabled={geocoding || !form.city.trim() || !form.state.trim()}
                className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg transition-all hover:opacity-80 disabled:opacity-40"
                style={{ background: '#EFF6FF', color: '#1C2E5E', border: '1px solid #DBEAFE' }}
              >
                {geocoding ? (
                  <div className="w-3 h-3 rounded-full border border-[#1C2E5E] border-t-transparent animate-spin" />
                ) : (
                  <Icon name="MapPinIcon" size={11} />
                )}
                {geocoding ? 'Locating...' : 'Auto-Geocode'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number"
                step="0.00001"
                value={form.lat}
                onChange={(e) => set('lat', e.target.value)}
                placeholder="Latitude (e.g. 39.9612)"
                className="w-full px-3 py-2.5 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-blue-200"
                style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
              />
              <input
                type="number"
                step="0.00001"
                value={form.lng}
                onChange={(e) => set('lng', e.target.value)}
                placeholder="Longitude (e.g. -82.9988)"
                className="w-full px-3 py-2.5 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-blue-200"
                style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
              />
            </div>
            {geocodeError && (
              <p className="text-xs mt-1.5" style={{ color: '#EF4444' }}>{geocodeError}</p>
            )}
          </div>

          {/* Size */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{ color: '#374151' }}>Competitor Size</label>
            <div className="grid grid-cols-3 gap-2">
              {(Object.entries(SIZE_CONFIG) as [CompetitorSize, typeof SIZE_CONFIG[CompetitorSize]][]).map(([key, cfg]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => set('size', key)}
                  className="flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl text-center transition-all"
                  style={{
                    background: form.size === key ? cfg.bg : '#F9FAFB',
                    border: `2px solid ${form.size === key ? cfg.color : '#E5E7EB'}`,
                  }}
                >
                  <span className="text-xs font-bold" style={{ color: form.size === key ? cfg.color : '#374151' }}>{cfg.label}</span>
                  <span className="text-[9px] leading-tight text-center" style={{ color: '#9CA3AF' }}>{cfg.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Source */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{ color: '#374151' }}>Data Source</label>
            <div className="grid grid-cols-3 gap-2">
              {(Object.entries(SOURCE_CONFIG) as [CompetitorSource, typeof SOURCE_CONFIG[CompetitorSource]][]).map(([key, cfg]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => set('source', key)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all"
                  style={{
                    background: form.source === key ? '#EFF6FF' : '#F9FAFB',
                    border: `1.5px solid ${form.source === key ? cfg.color : '#E5E7EB'}`,
                  }}
                >
                  <Icon name={cfg.icon as any} size={12} style={{ color: form.source === key ? cfg.color : '#9CA3AF' }} />
                  <span className="text-xs font-semibold" style={{ color: form.source === key ? cfg.color : '#6B7280' }}>{cfg.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Override toggle */}
          <div
            className="flex items-start gap-3 p-3 rounded-xl cursor-pointer"
            style={{ background: form.isOverride ? '#FEF3C7' : '#F9FAFB', border: `1.5px solid ${form.isOverride ? '#F59E0B' : '#E5E7EB'}` }}
            onClick={() => set('isOverride', !form.isOverride)}
          >
            <div
              className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ background: form.isOverride ? '#F59E0B' : '#E5E7EB' }}
            >
              {form.isOverride && <Icon name="CheckIcon" size={11} className="text-white" />}
            </div>
            <div>
              <div className="text-xs font-bold" style={{ color: form.isOverride ? '#D97706' : '#374151' }}>
                Mark as Override
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>
                Overrides Google Places data for this location with your ground-truth intelligence
              </div>
            </div>
          </div>

          {form.isOverride && (
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>Override Reason</label>
              <input
                type="text"
                value={form.overrideReason}
                onChange={(e) => set('overrideReason', e.target.value)}
                placeholder="e.g. Google Places data outdated — company closed Q1 2026"
                className="w-full px-3 py-2.5 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-yellow-200"
                style={{ borderColor: '#FDE68A', color: '#1C2E5E' }}
              />
            </div>
          )}

          {/* Optional intel */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
            <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              <Icon name="ChartBarIcon" size={12} style={{ color: '#6B7280' }} />
              <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#6B7280' }}>Market Intelligence (Optional)</span>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold mb-1" style={{ color: '#6B7280' }}>Rating (0–5)</label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={form.rating}
                  onChange={(e) => set('rating', e.target.value)}
                  placeholder="4.2"
                  className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-blue-200"
                  style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold mb-1" style={{ color: '#6B7280' }}>Review Count</label>
                <input
                  type="number"
                  min="0"
                  value={form.reviewCount}
                  onChange={(e) => set('reviewCount', e.target.value)}
                  placeholder="312"
                  className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-blue-200"
                  style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold mb-1" style={{ color: '#6B7280' }}>Est. Annual Revenue</label>
                <input
                  type="text"
                  value={form.annualRevenue}
                  onChange={(e) => set('annualRevenue', e.target.value)}
                  placeholder="$2.4M"
                  className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-blue-200"
                  style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold mb-1" style={{ color: '#6B7280' }}>Market Share Est.</label>
                <input
                  type="text"
                  value={form.marketShare}
                  onChange={(e) => set('marketShare', e.target.value)}
                  placeholder="~18%"
                  className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-blue-200"
                  style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-semibold mb-1" style={{ color: '#6B7280' }}>Street Address / Vicinity</label>
                <input
                  type="text"
                  value={form.vicinity}
                  onChange={(e) => set('vicinity', e.target.value)}
                  placeholder="1234 Main St, Columbus, OH"
                  className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-blue-200"
                  style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-semibold mb-1" style={{ color: '#6B7280' }}>Intelligence Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder="e.g. Aggressive storm chaser, primarily works insurance claims. Weak on commercial. Losing crews to higher wages."
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-blue-200 resize-none"
                  style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={onCancel}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all hover:bg-gray-100"
              style={{ color: '#6B7280', border: '1px solid #E5E7EB' }}
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(form)}
              disabled={!isValid || saving}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
            >
              {saving ? (
                <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
              ) : (
                <Icon name="FlagIcon" size={14} />
              )}
              {saving ? 'Saving...' : isEdit ? 'Update Competitor' : 'Add Competitor'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const CompetitorLocationsManager = ({ onCompetitorsChange, externalCompetitor, onExternalAdded }: CompetitorLocationsManagerProps) => {
  const [competitors, setCompetitors] = useState<ManualCompetitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [filterSize, setFilterSize] = useState<CompetitorSize | 'all'>('all');
  const [filterSource, setFilterSource] = useState<CompetitorSource | 'all'>('all');
  const [filterCity, setFilterCity] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const supabase = createClient();

  // ── Load from Supabase on mount ──────────────────────────────────────────────
  useEffect(() => {
    const loadCompetitors = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('expansion_competitors')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error && data) {
        const mapped = data.map(rowToCompetitor);
        setCompetitors(mapped);
      }
      setLoading(false);
    };

    loadCompetitors();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevExternalRef = useRef<ManualCompetitor | null>(null);

  // ── Notify parent whenever competitors list changes (must be outside render) ──
  useEffect(() => {
    if (!loading) {
      onCompetitorsChange?.(competitors);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitors, loading]);

  useEffect(() => {
    if (externalCompetitor && externalCompetitor !== prevExternalRef.current) {
      prevExternalRef.current = externalCompetitor;

      const upsertExternal = async () => {
        const alreadyExists = competitors.some((c) => c.id === externalCompetitor.id);
        if (alreadyExists) return;

        const { error } = await supabase
          .from('expansion_competitors')
          .upsert(competitorToRow(externalCompetitor), { onConflict: 'id' });

        if (!error) {
          setCompetitors((prev) => [externalCompetitor, ...prev]);
          onExternalAdded?.();
        }
      };

      upsertExternal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalCompetitor]);

  const editingCompetitor = editingId ? competitors.find((c) => c.id === editingId) : null;

  // Unique cities for filter
  const uniqueCities = Array.from(new Set(competitors.map((c) => `${c.city}, ${c.state}`))).sort();

  const handleSave = useCallback(async (form: typeof BLANK_FORM) => {
    setSaving(true);

    if (editingId) {
      const existing = competitors.find((c) => c.id === editingId);
      if (!existing) { setSaving(false); return; }

      const updated: ManualCompetitor = {
        ...existing,
        name: form.name.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        lat: parseFloat(form.lat as string),
        lng: parseFloat(form.lng as string),
        size: form.size,
        source: form.source,
        rating: form.rating ? parseFloat(form.rating as string) : undefined,
        reviewCount: form.reviewCount ? parseInt(form.reviewCount as string) : undefined,
        vicinity: form.vicinity.trim() || undefined,
        notes: form.notes.trim() || undefined,
        annualRevenue: form.annualRevenue.trim() || undefined,
        marketShare: form.marketShare.trim() || undefined,
        isOverride: form.isOverride,
        overrideReason: form.overrideReason.trim() || undefined,
      };

      const { error } = await supabase
        .from('expansion_competitors')
        .update(competitorToRow(updated))
        .eq('id', editingId);

      if (!error) {
        setCompetitors((prev) => prev.map((c) => (c.id === editingId ? updated : c)));
        setEditingId(null);
      }
    } else {
      const newComp: ManualCompetitor = {
        id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: form.name.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        lat: parseFloat(form.lat as string),
        lng: parseFloat(form.lng as string),
        size: form.size,
        source: form.source,
        rating: form.rating ? parseFloat(form.rating as string) : undefined,
        reviewCount: form.reviewCount ? parseInt(form.reviewCount as string) : undefined,
        vicinity: form.vicinity.trim() || undefined,
        notes: form.notes.trim() || undefined,
        annualRevenue: form.annualRevenue.trim() || undefined,
        marketShare: form.marketShare.trim() || undefined,
        isOverride: form.isOverride,
        overrideReason: form.overrideReason.trim() || undefined,
        createdAt: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('expansion_competitors')
        .insert(competitorToRow(newComp));

      if (!error) {
        setCompetitors((prev) => [newComp, ...prev]);
        setShowForm(false);
      }
    }

    setSaving(false);
  }, [editingId, competitors, supabase]);

  const handleDelete = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('expansion_competitors')
      .delete()
      .eq('id', id);

    if (!error) {
      setCompetitors((prev) => prev.filter((c) => c.id !== id));
      setDeleteConfirmId(null);
      if (expandedId === id) setExpandedId(null);
    }
  }, [expandedId, supabase]);

  const filtered = competitors.filter((c) => {
    if (filterSize !== 'all' && c.size !== filterSize) return false;
    if (filterSource !== 'all' && c.source !== filterSource) return false;
    if (filterCity !== 'all' && `${c.city}, ${c.state}` !== filterCity) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return c.name.toLowerCase().includes(q) || c.city.toLowerCase().includes(q) || c.state.toLowerCase().includes(q);
    }
    return true;
  });

  const counts = {
    large: competitors.filter((c) => c.size === 'large').length,
    medium: competitors.filter((c) => c.size === 'medium').length,
    small: competitors.filter((c) => c.size === 'small').length,
    overrides: competitors.filter((c) => c.isOverride).length,
  };

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', border: '1px solid #E5E7EB' }}
    >
      {/* ── Header ── */}
      <div
        className="flex items-center justify-between px-6 py-4"
        style={{ background: 'linear-gradient(135deg, #1F0A0A 0%, #7F1D1D 50%, #991B1B 100%)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.25)' }}>
            <Icon name="FlagIcon" size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white">Competitor Intelligence Manager</h2>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Manually add, edit &amp; override competitor locations beyond Google Places data
            </p>
          </div>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingId(null); }}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95"
          style={{ background: 'rgba(239,68,68,0.35)', border: '1.5px solid rgba(239,68,68,0.5)' }}
        >
          <Icon name="PlusIcon" size={15} />
          Add Competitor
        </button>
      </div>

      {/* ── Stats Strip ── */}
      <div className="grid grid-cols-4 divide-x" style={{ divideColor: '#F3F4F6', borderBottom: '1px solid #F3F4F6' }}>
        {[
          { label: 'Total Tracked', value: competitors.length, color: '#1C2E5E', bg: '#F8F9FA' },
          { label: 'Large Operators', value: counts.large, color: '#EF4444', bg: '#FEF2F2' },
          { label: 'Medium Operators', value: counts.medium, color: '#F59E0B', bg: '#FFFBEB' },
          { label: 'Overrides Active', value: counts.overrides, color: '#7C3AED', bg: '#F5F3FF' },
        ].map((stat) => (
          <div key={stat.label} className="px-5 py-3 text-center" style={{ background: stat.bg }}>
            <div className="text-xl font-black" style={{ color: stat.color }}>{stat.value}</div>
            <div className="text-[10px] font-semibold mt-0.5" style={{ color: '#9CA3AF' }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filters & Search ── */}
      <div className="px-6 py-3 flex items-center gap-3 flex-wrap" style={{ borderBottom: '1px solid #F3F4F6', background: '#FAFAFA' }}>
        {/* Search */}
        <div className="flex items-center gap-2 flex-1 min-w-[180px] px-3 py-2 rounded-xl" style={{ background: '#fff', border: '1px solid #E5E7EB' }}>
          <Icon name="MagnifyingGlassIcon" size={13} style={{ color: '#9CA3AF' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search competitors..."
            className="flex-1 text-xs outline-none bg-transparent"
            style={{ color: '#1C2E5E' }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{ color: '#9CA3AF' }}>
              <Icon name="XMarkIcon" size={12} />
            </button>
          )}
        </div>

        {/* City filter */}
        {uniqueCities.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>City:</span>
            <select
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold border outline-none"
              style={{
                background: filterCity !== 'all' ? '#EFF6FF' : '#F3F4F6',
                color: filterCity !== 'all' ? '#1C2E5E' : '#6B7280',
                border: `1px solid ${filterCity !== 'all' ? '#DBEAFE' : '#E5E7EB'}`,
              }}
            >
              <option value="all">All Cities</option>
              {uniqueCities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        {/* Size filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Size:</span>
          {(['all', 'large', 'medium', 'small'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterSize(s)}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
              style={{
                background: filterSize === s ? (s === 'all' ? '#1C2E5E' : SIZE_CONFIG[s as CompetitorSize]?.bg ?? '#F3F4F6') : '#F3F4F6',
                color: filterSize === s ? (s === 'all' ? '#fff' : SIZE_CONFIG[s as CompetitorSize]?.color ?? '#374151') : '#6B7280',
                border: `1px solid ${filterSize === s ? (s === 'all' ? '#1C2E5E' : SIZE_CONFIG[s as CompetitorSize]?.color ?? '#E5E7EB') : '#E5E7EB'}`,
              }}
            >
              {s === 'all' ? 'All' : SIZE_CONFIG[s as CompetitorSize].label}
            </button>
          ))}
        </div>

        {/* Source filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Source:</span>
          {(['all', 'manual', 'google_places', 'override'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterSource(s)}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
              style={{
                background: filterSource === s ? (s === 'all' ? '#1C2E5E' : '#EFF6FF') : '#F3F4F6',
                color: filterSource === s ? (s === 'all' ? '#fff' : SOURCE_CONFIG[s as CompetitorSource]?.color ?? '#374151') : '#6B7280',
                border: `1px solid ${filterSource === s ? (s === 'all' ? '#1C2E5E' : SOURCE_CONFIG[s as CompetitorSource]?.color ?? '#E5E7EB') : '#E5E7EB'}`,
              }}
            >
              {s === 'all' ? 'All Sources' : SOURCE_CONFIG[s as CompetitorSource].label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Competitor List ── */}
      <div className="divide-y" style={{ divideColor: '#F3F4F6' }}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 rounded-full border-2 border-red-500 border-t-transparent animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: 'linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)', border: '1px solid #FECACA' }}
            >
              <Icon name="FlagIcon" size={24} style={{ color: '#EF4444' }} />
            </div>
            <h3 className="text-sm font-bold mb-1" style={{ color: '#1C2E5E' }}>
              {competitors.length === 0 ? 'No Competitors Tracked Yet' : 'No Results Match Filters'}
            </h3>
            <p className="text-xs text-center max-w-xs" style={{ color: '#9CA3AF' }}>
              {competitors.length === 0
                ? 'Add competitor locations manually to build your market-specific intelligence database beyond what Google Places returns.'
                : 'Try adjusting your search or filter criteria.'}
            </p>
            {competitors.length === 0 && (
              <button
                onClick={() => { setShowForm(true); setEditingId(null); }}
                className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
              >
                <Icon name="PlusIcon" size={14} />
                Add First Competitor
              </button>
            )}
          </div>
        ) : (
          filtered.map((comp) => {
            const sizeCfg = SIZE_CONFIG[comp.size];
            const sourceCfg = SOURCE_CONFIG[comp.source];
            const isExpanded = expandedId === comp.id;
            const isDeleteConfirm = deleteConfirmId === comp.id;

            return (
              <div key={comp.id} style={{ background: isExpanded ? '#FAFAFA' : '#fff' }}>
                {/* Row */}
                <div className="flex items-center gap-4 px-6 py-4">
                  {/* Size indicator */}
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: sizeCfg.bg, border: `1.5px solid ${sizeCfg.color}` }}
                  >
                    <Icon name="FlagIcon" size={16} style={{ color: sizeCfg.color }} />
                  </div>

                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold truncate" style={{ color: '#1C2E5E' }}>{comp.name}</span>
                      {comp.isOverride && (
                        <span className="flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#FEF3C7', color: '#D97706', border: '1px solid #FDE68A' }}>
                          <Icon name="ExclamationTriangleIcon" size={8} />
                          OVERRIDE
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="text-xs" style={{ color: '#6B7280' }}>{comp.city}, {comp.state}</span>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: sizeCfg.bg, color: sizeCfg.color }}>{sizeCfg.label}</span>
                      <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: sourceCfg.color }}>
                        <Icon name={sourceCfg.icon as any} size={9} />
                        {sourceCfg.label}
                      </span>
                      {comp.rating != null && (
                        <span className="text-[10px] font-semibold" style={{ color: '#F59E0B' }}>⭐ {comp.rating} ({comp.reviewCount ?? 0})</span>
                      )}
                    </div>
                  </div>

                  {/* Quick intel badges */}
                  <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                    {comp.annualRevenue && (
                      <div className="text-center px-2.5 py-1.5 rounded-lg" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                        <div className="text-xs font-black" style={{ color: '#10B981' }}>{comp.annualRevenue}</div>
                        <div className="text-[9px]" style={{ color: '#9CA3AF' }}>Revenue</div>
                      </div>
                    )}
                    {comp.marketShare && (
                      <div className="text-center px-2.5 py-1.5 rounded-lg" style={{ background: '#EFF6FF', border: '1px solid #DBEAFE' }}>
                        <div className="text-xs font-black" style={{ color: '#1C2E5E' }}>{comp.marketShare}</div>
                        <div className="text-[9px]" style={{ color: '#9CA3AF' }}>Mkt Share</div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : comp.id)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:bg-gray-100"
                      style={{ color: '#6B7280' }}
                      title={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={14} />
                    </button>
                    <button
                      onClick={() => { setEditingId(comp.id); setShowForm(false); }}
                      className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:bg-blue-50"
                      style={{ color: '#1C2E5E' }}
                      title="Edit"
                    >
                      <Icon name="PencilSquareIcon" size={14} />
                    </button>
                    {isDeleteConfirm ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(comp.id)}
                          className="px-2 py-1 rounded-lg text-xs font-bold text-white"
                          style={{ background: '#EF4444' }}
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="px-2 py-1 rounded-lg text-xs font-semibold"
                          style={{ background: '#F3F4F6', color: '#6B7280' }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirmId(comp.id)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:bg-red-50"
                        style={{ color: '#EF4444' }}
                        title="Delete"
                      >
                        <Icon name="TrashIcon" size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="px-6 pb-5 pt-1">
                    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
                      {/* Top 4-col grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y" style={{ divideColor: '#F3F4F6' }}>
                        {[
                          { label: 'Coordinates', value: `${comp.lat.toFixed(4)}, ${comp.lng.toFixed(4)}`, icon: 'MapPinIcon', color: '#1C2E5E' },
                          { label: 'Size Category', value: sizeCfg.label, icon: 'BuildingOffice2Icon', color: sizeCfg.color },
                          { label: 'Data Source', value: sourceCfg.label, icon: sourceCfg.icon, color: sourceCfg.color },
                          { label: 'Added', value: new Date(comp.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), icon: 'CalendarDaysIcon', color: '#6B7280' },
                        ].map((item) => (
                          <div key={item.label} className="px-4 py-3" style={{ background: '#FAFAFA' }}>
                            <div className="flex items-center gap-1.5 mb-1">
                              <Icon name={item.icon as any} size={10} style={{ color: item.color }} />
                              <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>{item.label}</span>
                            </div>
                            <div className="text-xs font-semibold" style={{ color: '#1C2E5E' }}>{item.value}</div>
                          </div>
                        ))}
                      </div>

                      {/* Business Details Section */}
                      {(comp.phone || comp.website || comp.yearsInBusiness || comp.knownOwners) && (
                        <div className="grid grid-cols-2 md:grid-cols-4 divide-x" style={{ divideColor: '#F3F4F6', borderTop: '1px solid #F3F4F6' }}>
                          {comp.phone && (
                            <div className="px-4 py-3">
                              <div className="flex items-center gap-1.5 mb-1">
                                <Icon name="PhoneIcon" size={10} style={{ color: '#10B981' }} />
                                <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Phone</span>
                              </div>
                              <a href={`tel:${comp.phone}`} className="text-xs font-semibold hover:underline" style={{ color: '#10B981' }}>{comp.phone}</a>
                            </div>
                          )}
                          {comp.website && (
                            <div className="px-4 py-3">
                              <div className="flex items-center gap-1.5 mb-1">
                                <Icon name="GlobeAltIcon" size={10} style={{ color: '#6366F1' }} />
                                <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Website</span>
                              </div>
                              <a href={comp.website} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold hover:underline truncate block max-w-[140px]" style={{ color: '#6366F1' }}>
                                {comp.website.replace(/^https?:\/\/(www\.)?/, '')}
                              </a>
                            </div>
                          )}
                          {comp.yearsInBusiness && (
                            <div className="px-4 py-3">
                              <div className="flex items-center gap-1.5 mb-1">
                                <Icon name="ClockIcon" size={10} style={{ color: '#F59E0B' }} />
                                <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Years in Business</span>
                              </div>
                              <div className="text-xs font-semibold" style={{ color: '#1C2E5E' }}>{comp.yearsInBusiness}</div>
                            </div>
                          )}
                          {comp.knownOwners && (
                            <div className="px-4 py-3">
                              <div className="flex items-center gap-1.5 mb-1">
                                <Icon name="UserIcon" size={10} style={{ color: '#7C3AED' }} />
                                <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Known Owners</span>
                              </div>
                              <div className="text-xs font-semibold" style={{ color: '#1C2E5E' }}>{comp.knownOwners}</div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Specialties + Weaknesses */}
                      {(comp.specialties || comp.competitiveWeaknesses) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-x" style={{ divideColor: '#F3F4F6', borderTop: '1px solid #F3F4F6' }}>
                          {comp.specialties && (
                            <div className="px-4 py-3">
                              <div className="flex items-center gap-1.5 mb-1">
                                <Icon name="WrenchScrewdriverIcon" size={10} style={{ color: '#1C2E5E' }} />
                                <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Specialties</span>
                              </div>
                              <div className="text-xs" style={{ color: '#374151' }}>{comp.specialties}</div>
                            </div>
                          )}
                          {comp.competitiveWeaknesses && (
                            <div className="px-4 py-3" style={{ background: '#FFF7ED' }}>
                              <div className="flex items-center gap-1.5 mb-1">
                                <Icon name="ExclamationCircleIcon" size={10} style={{ color: '#EA580C' }} />
                                <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#EA580C' }}>Competitive Weakness</span>
                              </div>
                              <div className="text-xs" style={{ color: '#9A3412' }}>{comp.competitiveWeaknesses}</div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Business Hours */}
                      {comp.businessHours && comp.businessHours.length > 0 && (
                        <div className="px-4 py-3" style={{ borderTop: '1px solid #F3F4F6' }}>
                          <div className="flex items-center gap-1.5 mb-2">
                            <Icon name="CalendarDaysIcon" size={10} style={{ color: '#6B7280' }} />
                            <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Business Hours</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-0.5">
                            {comp.businessHours.map((line, idx) => (
                              <div key={idx} className="text-[10px]" style={{ color: '#374151' }}>{line}</div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Address / Vicinity, Override Reason, Notes */}
                      {(comp.formattedAddress || comp.vicinity || comp.notes || comp.overrideReason) && (
                        <div className="divide-y" style={{ divideColor: '#F3F4F6', borderTop: '1px solid #F3F4F6' }}>
                          {(comp.formattedAddress || comp.vicinity) && (
                            <div className="px-4 py-3">
                              <div className="flex items-center gap-1.5 mb-1">
                                <Icon name="MapPinIcon" size={10} style={{ color: '#6B7280' }} />
                                <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Full Address</span>
                              </div>
                              <div className="text-xs" style={{ color: '#374151' }}>{comp.formattedAddress ?? comp.vicinity}</div>
                              {comp.googleMapsUrl && (
                                <a href={comp.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold hover:underline" style={{ color: '#1C2E5E' }}>
                                  <Icon name="MapIcon" size={9} />
                                  View on Google Maps
                                </a>
                              )}
                            </div>
                          )}
                          {comp.overrideReason && (
                            <div className="px-4 py-3" style={{ background: '#FFFBEB' }}>
                              <div className="flex items-center gap-1.5 mb-1">
                                <Icon name="ExclamationTriangleIcon" size={10} style={{ color: '#D97706' }} />
                                <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#D97706' }}>Override Reason</span>
                              </div>
                              <div className="text-xs" style={{ color: '#92400E' }}>{comp.overrideReason}</div>
                            </div>
                          )}
                          {comp.notes && (
                            <div className="px-4 py-3">
                              <div className="text-[9px] font-bold uppercase tracking-wide mb-1" style={{ color: '#9CA3AF' }}>Intelligence Notes</div>
                              <div className="text-xs leading-relaxed" style={{ color: '#374151' }}>{comp.notes}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ── Footer hint ── */}
      {competitors.length > 0 && (
        <div className="px-6 py-3 flex items-center justify-between" style={{ borderTop: '1px solid #F3F4F6', background: '#FAFAFA' }}>
          <span className="text-xs" style={{ color: '#9CA3AF' }}>
            {filtered.length} of {competitors.length} competitors shown
          </span>
          <div className="flex items-center gap-1.5 text-xs" style={{ color: '#9CA3AF' }}>
            <Icon name="InformationCircleIcon" size={12} />
            <span>Manual entries supplement Google Places data on the Competitor Density overlay</span>
          </div>
        </div>
      )}

      {/* ── Add Form Modal ── */}
      {showForm && !editingId && (
        <CompetitorForm
          title="Add Competitor Location"
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
          saving={saving}
        />
      )}

      {/* ── Edit Form Modal ── */}
      {editingId && editingCompetitor && (
        <CompetitorForm
          title={`Edit — ${editingCompetitor.name}`}
          isEdit
          initial={{
            name: editingCompetitor.name,
            city: editingCompetitor.city,
            state: editingCompetitor.state,
            lat: String(editingCompetitor.lat),
            lng: String(editingCompetitor.lng),
            size: editingCompetitor.size,
            source: editingCompetitor.source,
            rating: editingCompetitor.rating != null ? String(editingCompetitor.rating) : '',
            reviewCount: editingCompetitor.reviewCount != null ? String(editingCompetitor.reviewCount) : '',
            vicinity: editingCompetitor.vicinity ?? '',
            notes: editingCompetitor.notes ?? '',
            annualRevenue: editingCompetitor.annualRevenue ?? '',
            marketShare: editingCompetitor.marketShare ?? '',
            isOverride: editingCompetitor.isOverride ?? false,
            overrideReason: editingCompetitor.overrideReason ?? '',
          }}
          onSave={handleSave}
          onCancel={() => setEditingId(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

export default CompetitorLocationsManager;
